/** The hours editor's drag maths (the discussion polish and hours editor round, 2026-11-02; brief
 *  docs/briefs/discussion-polish-hours-editor-round.md rule 8, DECISIONS "2026-11-02 — The hours editor's drag"). Pure: pixels down a day
 *  column become minutes in half-hour steps, and a block's edge or the whole block moves within its day without crossing its neighbours.
 *  Every function returns a new list and leaves the one given as it was. */
export type WeekBlock = { from: string; to: string };

const STEP = 30;
const DAY_END = 24 * 60;
/** minutes per pixel at 20 px an hour */
const MINUTES_PER_PX = 3;
const minutesOf = (hhmm: string) => { const [h, m] = hhmm.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };
const timeOf = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
const roundToStep = (minutes: number) => Math.round(minutes / STEP) * STEP;
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
/** the room a block may use: from the previous block's end (or midnight) to the next block's start (or 24:00) */
const roomOf = (blocks: WeekBlock[], index: number) => ({ lo: blocks[index - 1] ? minutesOf(blocks[index - 1]!.to) : 0, hi: blocks[index + 1] ? minutesOf(blocks[index + 1]!.from) : DAY_END });
const replaced = (blocks: WeekBlock[], index: number, from: number, to: number): WeekBlock[] => blocks.map((b, i) => (i === index ? { from: timeOf(from), to: timeOf(to) } : { ...b }));

/** pixels from a day column's top to minutes, rounded to the nearest half hour, held inside 00:00 to 24:00 */
export const weekMinuteAt = (y: number): number => clamp(roundToStep(y * MINUTES_PER_PX), 0, DAY_END);

/** one edge of block `index` to `minute` (rounded to the half hour), never closer than half an hour to the block's other edge, never past a
 *  neighbouring block or the day's ends */
export function weekMoveEdge(blocks: WeekBlock[], index: number, edge: "from" | "to", minute: number): WeekBlock[] {
  const b = blocks[index];
  if (!b) return blocks.map((x) => ({ ...x }));
  const from = minutesOf(b.from), to = minutesOf(b.to), room = roomOf(blocks, index), m = roundToStep(minute);
  return edge === "from" ? replaced(blocks, index, clamp(m, room.lo, to - STEP), to) : replaced(blocks, index, from, clamp(m, from + STEP, room.hi));
}

/** the whole block `index` by `deltaMinutes` (rounded to the half hour), its length kept, stopped by its neighbours and the day's ends */
export function weekMoveBlock(blocks: WeekBlock[], index: number, deltaMinutes: number): WeekBlock[] {
  const b = blocks[index];
  if (!b) return blocks.map((x) => ({ ...x }));
  const from = minutesOf(b.from), length = minutesOf(b.to) - from, room = roomOf(blocks, index);
  if (room.hi - room.lo < length) return blocks.map((x) => ({ ...x }));
  const start = clamp(from + roundToStep(deltaMinutes), room.lo, room.hi - length);
  return replaced(blocks, index, start, start + length);
}
